import React, { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as passengerSlice from '../../../data/passenger.slice';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import passengerUtils from '../../../services/passengerUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Passenger } from '../../../data/models/passenger.model';
import ProfileForm from './ProfileForm';
import { Client } from '../../../data/models/client.model';
import { Gender } from '../../../data/models/gender.model';
import { PassengerAuthorizationReason } from '../../../data/models/passenger-authorization-reason.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { useOnSaved } from '../../../hooks/useOnSaved';

const PREFIX = 'PassengerProfileDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: 460,
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(0),
  },
}));

interface PassengerProfileDialogProps {
  passenger?: Passenger;
  title: string;
  portalClientBC?: string;
  clients: Client[];
  genders: Gender[];
  passengerAuthorizationReasons: PassengerAuthorizationReason[];
  open: boolean;
  close: () => void;
}

export default function PassengerProfileDialog({
  passenger,
  title,
  portalClientBC,
  clients,
  genders,
  passengerAuthorizationReasons,
  open,
  close,
}: PassengerProfileDialogProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentPassenger = useAppSelector(
    passengerSlice.selectCurrentPassenger,
  );
  const savedPassenger = useAppSelector(passengerSlice.selectSavedPassenger);
  const savingPassenger = useAppSelector(passengerSlice.selectSavingPassenger);
  const confirmDialog = useConfirmDialog();

  const defaultValues = useMemo(
    () => passengerUtils.getProfileDefaults(passenger),
    [passenger],
  );
  const formHook = useForm({
    mode: 'onTouched',
    defaultValues,
  });

  const {
    formState: { isDirty },
  } = formHook;

  React.useEffect(() => {
    const defaults = passengerUtils.getProfileDefaults(passenger);
    formHook.reset(defaults);
  }, [passenger]);

  useEffect(() => {
    formHook.reset(defaultValues);
  }, [defaultValues]);

  useOnSaved(savedPassenger, () => {
    handleFormclose();
    navigate(`/passengers/${currentPassenger?.passengerUUID}`);
  });

  const confirm = () => {
    formHook.reset();
    close();
  };

  const handleFormclose = () => {
    close();
    formHook.reset();
  };

  const closeModal = () => {
    confirmDialog.request({
      title: t('passenger.confirmCancelCreatePassengerTitle'),
      message: t('passenger.confirmCancelCreatePassengerMessage'),
      onConfirm: confirm,
    });
  };

  const isFormDirty = () => {
    if (isDirty) {
      closeModal();
    } else {
      confirm();
    }
  };

  const handleSave = (data: any) => {
    const passengerData = passengerUtils.getPassengerFromFormData(
      data,
      passenger,
    );
    if (passengerData.passengerUUID) {
      dispatch(passengerSlice.updatePassengerAsync(passengerData));
    } else {
      dispatch(passengerSlice.createPassengerAsync(passengerData));
    }
  };

  return (
    <RootDialog open={open} onClose={isFormDirty}>
      <DialogTitle>
        {title}
        {savingPassenger && (
          <LinearProgress style={{ width: '100%' }} color="primary" />
        )}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <ProfileForm
            currentClientBC={passenger?.clientBC}
            portalClientBC={portalClientBC}
            clients={clients}
            genders={genders}
            passengerAuthorizationReasons={passengerAuthorizationReasons}
            formHook={formHook}
          />
        </LocalizationProvider>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={isFormDirty}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !formHook.formState.isValid ||
            !formHook.formState.isDirty ||
            savingPassenger
          }
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.create')}
        </Button>
      </DialogActions>
      <ConfirmationDialog {...confirmDialog.dialogProps} />
    </RootDialog>
  );
}
